import { DeliveryFeeDiscountOption } from '../../../../types/global';
import {
  AllowedTimeOfDay,
  AllowedWeekdays,
  CampaignTypeForAccounting,
  ClaimCodeType,
  LocalMarketingCampaignDiscountType,
} from '@kotipizzagroup/kotipizza-campaigns-api-client';

export type InvoicingDetails = {
  invoicingDetails: InvoicingInfo;
};

export type IsUnlimitedPublicCodes = {
  isUnlimitedPublicCodes: boolean | null;
};

export type LocalMarketingInfo = {
  discountType: LocalMarketingCampaignDiscountType;
  discountValue: number;
  campaignAccountingType: CampaignTypeForAccounting;
  campaignCodeType: CampaignCodeType;
  requireLogIn: boolean;
  shopExternalIds: string[];
  deliveryMethods: string[];
  restrictedProductSizes: number[];
  personalCodeInfo?: PersonalCodeInfo;
  publicCode?: string;
  uniqueCodeCount?: number;
  uniqueCodePrefix?: string;
  publicUsageLimit?: number;
  minimumOrderValue?: number;
  allowedTimeOfDay?: AllowedTimeOfDay;
  allowedWeekdays?: AllowedWeekdays;
  restrictedProducts?: number[];
  excludeModified?: boolean;
  deliveryFeeDiscountOption?: DeliveryFeeDiscountOption;
};

export type CampaignFreeDeliveryInfo = {
  name: string;
  requireLogIn: boolean;
  isAppOnly: boolean;
  disablePreordersOnCampaign: boolean;
  isOffInLunchTime: boolean;
  minimumOrderValue: number;
  start: string;
  end: string;
  description: string;
  added: string;
  shopExternalIds?: string[];
  weekdays?: number[];
};

export type CampaignBasicInfo = {
  name: string;
  combinable: boolean;
  requireLogIn: boolean;
  isAppOnly: boolean;
  applyDiscountOnDelivery: boolean;
  start: string;
  end: string;
  description: string;
  added: string;
  allowedTimeOfDay: number;
  allowedWeekdays: number;
  campaignTypeForAnalytics?: CampaignTypeForAnalytics;
};

export type PersonalCodeInfo = {
  uniqueCodeCount: number;
  uniqueCodePrefix: string;
  discountAmount: number;
};

export type DisablePreordersOnCampaign = {
  disablePreordersOnCampaign: boolean;
};

export type InvoicingInfo = {
  businessId: string;
  companyName: string;
  contactPerson: string;
  invoiceReferenceNumber: string;
  billingDate?: string;
  billingSurchargeAmount?: string;
  billingType?: string;
  billingRequirePrepaid?: boolean;
};

export type RestaurantClaimCodesInfo = {
  start: string;
  end: string;
  claimCodesConfigs: ClaimCodesConfig[];
  shopExternalIds: string[];
  codesValidInAllShops: string; // Using string as bools get surprisingly converted to strings: https://github.com/react-hook-form/react-hook-form/issues/2241 and https://github.com/mui/material-ui/issues/13250
  allowUnregisteredCustomer: boolean;
  groupPrefix?: string;
  campaignNameSuffix?: string;
};

export type ClaimCodesConfig = {
  amountOfCodes: number;
  claimCodeType: ClaimCodeType;
};

export type ProductRestriction = {
  restrictedSizes?: number[];
  restricedProducts?: number[];
};

export type WarehouseItemCategoryInfo = {
  warehouseItemCategoryId?: number | null;
};

export type B2bCampaign = CampaignBasicInfo & PersonalCodeInfo & ProductRestriction;

export enum CampaignTypeForAnalytics {
  Undefined = 0,
  LunchDrink = 1,
  FreeDelivery = 2,
  ProductCoupon = 3,
  ProductDiscount = 4,
  B2BPrepaid = 5,
  ClaimCode = 6,
  LocalMarketingCampaign = 7,
  LoyaltyPrizes = 8,
  CrossSell = 9,
  LoyaltyTargetedMarketing = 10,
  BuySomeGetSome = 11,
}

export enum CampaignCodeType {
  Public = 3,
  Personal = 2,
  NoCode = 1,
}

export enum CampaignBillingType {
  OrderedCodes,
  UsedCodes,
}

export enum DeliveryFeeDiscountType {
  None = 0,
  FixedPrice = 1,
}

export enum CustomerCategoryType {
  NoOrdersInLast12Months = 0,
  Single = 1,
  Couple = 2,
  Family = 3,
  Group = 4,
  Hybrid = 5,
}

export enum BuyingFrequencyClassType {
  Passive = 0,
  Light = 1,
  MediumLower = 2,
  Medium = 3,
  MediumUpper = 4,
  Heavy = 5,
  SuperHeavy = 6,
}
